import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import Container from "../layout/Container";
import {useTheme} from "connectui/theme/ThemeProvider";

const defaultAnchorOrigin = {vertical: "bottom", horizontal: "end"};
const defaultModalPosition = {vertical: "top", horizontal: "start"};

/**
 * ModalPortal allows you to render a child component into a different part of the DOM.
 * Intended to be used for modals and menus that need to be positioned
 * without the restrictions of the parent hierarchy.
 *
 * @param {Object} props - The props for the ModalPortal component.
 * @param {boolean} props.isOpen - Controls the open state of the portal.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * @param {React.ReactNode} [props.anchorEl=null] - The element that will serve as an anchor to position the portal.
 * @param {React.ReactNode|Function} props.children - The content of the portal.
 * @param {Object} props.customPosition - Custom values for portal positioning ({top, right, bottom, left}).
 * @param {Object} [props.anchorOrigin={vertical: "bottom", horizontal: "start"}] - Anchor point of the triggering element to which the modal will be attached. Defaults to {vertical: "bottom", horizontal: "start"}.
 * @param {"top"|"bottom"} [props.anchorOrigin.vertical="bottom"] - Vertical position of the anchor point.
 * @param {"start"|"end"} [props.anchorOrigin.horizontal="start"] - Horizontal position of the anchor point.
 * @param {Object} [props.modalPosition={vertical: "top", horizontal: "start"}] - Modal anchor point. Defaults to {vertical: "top", horizontal: "start"}.
 * @param {"top"|"bottom"} [props.modalPosition.vertical="top"] - Vertical position of the modal anchor point.
 * @param {"start"|"end"} [props.modalPosition.horizontal="start"] - Horizontal position of the modal anchor point.
 *
 * @returns {JSX.Element} The rendered ModalPortal component.
 */
const ModalPortal = ({
  isOpen,
  onClose,
  anchorEl = null,
  children,
  customPosition,
  anchorOrigin = defaultAnchorOrigin,
  modalPosition = defaultModalPosition,
  ...props
}) => {
  const wrapperRef = useRef(null);
  const {theme} = useTheme();
  const [position, setPosition] = useState(
    customPosition ?? {top: -9999, left: -9999},
  );
  const [contentRef, setContentRef] = useState(null);

  useEffect(() => {
    if (!customPosition) return;
    setPosition(customPosition);
  }, [customPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const childrenNode = contentRef || wrapperRef.current;
    if (!isOpen || !anchorEl || !childrenNode || !!customPosition) return;

    // Combine default and custom configurations
    const anchorPos = {...defaultAnchorOrigin, ...anchorOrigin};
    const modalPos = {...defaultModalPosition, ...modalPosition};

    const {top, bottom, left, right} = anchorEl.getBoundingClientRect();
    const contentWidth = childrenNode.offsetWidth;
    const contentHeight = childrenNode.offsetHeight;

    // Determine base values based on the anchor orientation
    const verticalValue = anchorPos.vertical === "top" ? top : bottom;
    const horizontalValue = anchorPos.horizontal === "start" ? left : right;

    // Check if content fits in available space
    const canFitAbove = verticalValue >= contentHeight;
    const canFitBelow = window.innerHeight - verticalValue >= contentHeight;
    const canFitLeft = horizontalValue >= contentWidth;
    const canFitRight = window.innerWidth - horizontalValue >= contentWidth;

    // Determine if need to flip positions
    const flipVertical =
      (modalPos.vertical === "top" && !canFitBelow) ||
      (modalPos.vertical === "bottom" && !canFitAbove);
    const flipHorizontal =
      (modalPos.horizontal === "start" && !canFitRight) ||
      (modalPos.horizontal === "end" && !canFitLeft);

    // Calculate vertical (Y) position
    let yKey, yValue;
    if (flipVertical) {
      yKey = modalPos.vertical === "top" ? "bottom" : "top";
      yValue =
        modalPos.vertical === "bottom"
          ? verticalValue
          : window.innerHeight - verticalValue;
    } else {
      yKey = modalPos.vertical;
      yValue =
        modalPos.vertical === "top"
          ? verticalValue
          : window.innerHeight - verticalValue;
    }

    // Calculate horizontal (X) position
    let xKey, xValue;
    if (flipHorizontal) {
      xKey = modalPos.horizontal === "start" ? "right" : "left";
      xValue =
        modalPos.horizontal === "end"
          ? horizontalValue
          : window.innerWidth - horizontalValue;
    } else {
      xKey = modalPos.horizontal === "start" ? "left" : "right";
      xValue =
        modalPos.horizontal === "start"
          ? horizontalValue
          : window.innerWidth - horizontalValue;
    }

    setPosition({[xKey]: xValue, [yKey]: yValue});
  }, [isOpen, anchorEl, contentRef, customPosition]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <Container
      ref={wrapperRef}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        zIndex: theme.zIndex.modal,
        ...position,
      }}
      {...props}
    >
      {typeof children === "function"
        ? children({ref: setContentRef})
        : children}
    </Container>,
    document.body,
  );
};

ModalPortal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.any,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  customPosition: PropTypes.object,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "bottom"]),
    horizontal: PropTypes.oneOf(["start", "end"]),
  }),
  modalPosition: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "bottom"]),
    horizontal: PropTypes.oneOf(["start", "end"]),
  }),
};

export default ModalPortal;
